import React from 'react';
import { Trash2, Plus, ChevronDown } from 'lucide-react';

export const cn = (...classes: any[]) => classes.filter(Boolean).join(' ');

export const ScrollContainer = ({ children, className }: { children: React.ReactNode, className?: string }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const topScrollRef = React.useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = React.useState(0);

  React.useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContentWidth(containerRef.current.scrollWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    const observer = new MutationObserver(updateWidth);
    if (containerRef.current) observer.observe(containerRef.current, { childList: true, subtree: true });
    return () => {
      window.removeEventListener('resize', updateWidth);
      observer.disconnect();
    };
  }, []);

  const onTopScroll = (e: any) => {
    if (containerRef.current) containerRef.current.scrollLeft = e.target.scrollLeft;
  };

  const onContainerScroll = (e: any) => {
    if (topScrollRef.current) topScrollRef.current.scrollLeft = e.target.scrollLeft;
  };

  return (
    <div className={cn("relative group/scroll", className)}>
      {/* Top Scrollbar Mirror */}
      <div 
        ref={topScrollRef}
        onScroll={onTopScroll}
        className="overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-emerald-500/20 scrollbar-track-transparent h-2 mb-1"
      >
        <div style={{ width: contentWidth }} className="h-px"></div>
      </div>
      
      {/* Main Content */}
      <div 
        ref={containerRef}
        onScroll={onContainerScroll}
        className="overflow-x-auto scrollbar-thin scrollbar-thumb-emerald-500/40 scrollbar-track-transparent"
      >
        {children}
      </div>
    </div>
  );
};

export const COL_WIDTH_OPTIONS = [
  { id: 'nano', thClass: 'min-w-[40px]', tdClass: 'min-w-[40px] text-center', label: 'NANO' },
  { id: 'xxs', thClass: 'min-w-[80px]', tdClass: 'min-w-[80px] text-center', label: 'XXS' },
  { id: 'xs', thClass: 'min-w-[120px]', tdClass: 'min-w-[120px] text-center', label: 'XS' },
  { id: 'sm', thClass: 'min-w-[180px]', tdClass: 'min-w-[180px] text-center', label: 'SM' },
  { id: 'md', thClass: 'min-w-[260px]', tdClass: 'min-w-[260px]', label: 'MD' },
  { id: 'lg', thClass: 'min-w-[400px]', tdClass: 'min-w-[400px]', label: 'LG' },
  { id: 'xl', thClass: 'min-w-[650px]', tdClass: 'min-w-[650px]', label: 'XL' },
];

export const getColWidthOption = (col: any) => {
  const lbl = (col.label || col.key || '').toUpperCase();
  if (lbl.includes('NO BENEFICIO') || lbl.includes('NO. ASPECTO') || 
      lbl.includes('NO OBJETIVO') || lbl.includes('NO. OBJETIVO') || 
      lbl.includes('NO ACCION') || lbl.includes('NO. ACCION') || 
      lbl.includes('NO.') || lbl.includes('ID') || lbl.includes('#') || lbl.includes('NUM')) {
    return COL_WIDTH_OPTIONS[0]; // NANO
  }
  const w = col.colWidth || (col.isNarrow ? 'sm' : 'md');
  return COL_WIDTH_OPTIONS.find(o => o.id === w) || COL_WIDTH_OPTIONS[4];
};

export const cycleColWidth = (col: any) => {
  const current = getColWidthOption(col);
  const idx = COL_WIDTH_OPTIONS.findIndex(o => o.id === current.id);
  const next = COL_WIDTH_OPTIONS[(idx + 1) % COL_WIDTH_OPTIONS.length];
  return { ...col, colWidth: next.id, isNarrow: undefined };
};

export const GenericTableEditor = ({ title, onTitleChange, columns, data, onChange, readOnly, isAdmin, onColumnsChange, canEditRows }: { title?: string, onTitleChange?: (val: string) => void, columns: any[], data: any[], onChange: (data: any[]) => void, readOnly?: boolean, isAdmin?: boolean, onColumnsChange?: (cols: any[]) => void, canEditRows?: boolean }) => {
  const items = Array.isArray(data) ? data : [{}];
  const canEditStructure = isAdmin; const canEditData = !readOnly || isAdmin; const effectiveCanEditRows = canEditRows !== undefined ? canEditRows : canEditStructure;

  // 1. AUDITORÍA DE ESTRUCTURA: Garantizar Unicidad de Keys en tiempo de renderizado
  const sanitizedColumns = React.useMemo(() => {
    const seen = new Set();
    return columns.map((col, idx) => {
      let key = col.key;
      if (!key || key.trim() === '') {
        const base = (col.label || 'col').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        key = `${base || 'field'}_${idx}`;
      }
      let finalKey = key;
      let counter = 1;
      while (seen.has(finalKey)) {
        finalKey = `${key}_${counter}`;
        counter++;
      }
      seen.add(finalKey);
      return { ...col, key: finalKey };
    });
  }, [columns]);

  // 2. STRUCTURAL SYNC: Persistir las llaves generadas de vuelta al store global
  React.useEffect(() => {
    if (!canEditStructure || !onColumnsChange) return;
    const hasChanges = sanitizedColumns.some((sc, idx) => sc.key !== columns[idx]?.key);
    if (hasChanges) {
        console.log("[GenericTableEditor] Syncing generated keys to global state...");
        onColumnsChange(sanitizedColumns);
    }
  }, [sanitizedColumns, columns, onColumnsChange, canEditStructure]);

  const updateItem = (index: number, field: string, value: any) => {
    if (!canEditData) return;
    
    if (!field) {
        console.error("[GenericTableEditor] Intento de actualizar campo sin KEY válida.");
        return;
    }
    
    // AUDITORÍA DE DIAGNÓSTICO (Caso CONVENIOS/Dynamic)
    console.group(`[GenericTableEditor] Edit Row ${index} - Field: ${field}`);
    console.log("Current Value:", items[index]?.[field]);
    console.log("New Value:", value);
    console.log("Full Row State BEFORE:", { ...items[index] });
    
    // CLONACIÃ“N PROFUNDA Y ACTUALIZACIÃ“N AISLADA
    const newItems = items.map((item, i) => {
      if (i === index) {
        // Blindaje extra: asegurar que el objeto no sea nulo
        const safeItem = item || {};
        const updated = { ...safeItem, [field]: value };
        
        // Verificación de integridad: si el valor cambió de vacío a algo, o es un cambio real
        return updated;
      }
      return item;
    });
    
    console.log("Full Row State AFTER:", { ...newItems[index] });
    console.log("Integrity Check (Col 2 preserved?):", 
      Object.keys(newItems[index]).length >= Object.keys(items[index] || {}).length);
    console.groupEnd();

    onChange(newItems);
  };

  const addItem = () => {
    if (!effectiveCanEditRows) return;
    const cleanItems = items.filter(i => i !== null && i !== undefined);
    
    // RECONCILIACIÃ“N ESTRUCTURAL: Inicializar con TODAS las llaves sanitizadas
    const template: any = {};
    sanitizedColumns.forEach(c => { 
      if (c.key && !c.key.startsWith('_')) {
        template[c.key] = ''; 
      }
    });
    
    onChange([...cleanItems, { ...template }]); 
  };
  
  const removeItem = (index: number) => {
    if (!effectiveCanEditRows) return;
    if (!confirm('¿Estás seguro de que deseas eliminar este registro?')) return;
    
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems.length > 0 ? newItems : []);
  };

  return (
    <div className="w-full pb-3 rounded-2xl border border-slate-800 bg-slate-900/40 backdrop-blur-xl group/table shadow-2xl overflow-hidden">
      {title !== undefined && (
        <div className="px-4 pt-4 pb-2">
          {(isAdmin && !readOnly && onTitleChange) ? (
            <input 
              className="bg-transparent text-xl font-black text-white uppercase border-b border-dashed border-slate-700 hover:border-emerald-500 focus:outline-none w-full min-h-[44px]"
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
              placeholder="TÍTULO DE LA TABLA"
            />
          ) : (
            <h3 className="text-xl font-black text-white uppercase">{title}</h3>
          )}
        </div>
      )}
      
      <ScrollContainer className="px-1">
        <table className="w-full border-collapse text-left min-w-max">
        <thead className="sticky top-0 z-20 text-[10px] uppercase bg-slate-950 font-black tracking-widest text-white/40">
          <tr>
            <th className="px-4 py-3 border-r border-b border-slate-800 w-24 text-center bg-slate-950">#</th>
            {sanitizedColumns.filter(c => !c.key.startsWith('_')).map(col => (
              <th key={col.key} className={cn("px-4 py-3 border-r border-b border-slate-800 bg-slate-900/60 group/th align-top relative", getColWidthOption(col).thClass)}>
                <div className="flex justify-between items-start gap-2 h-full">
                  <div className="flex-grow">
                {(isAdmin && onColumnsChange) ? (
                  <textarea
                    className="bg-transparent w-full focus:outline-none focus:text-emerald-400 placeholder:text-white/30 font-black resize-none overflow-hidden leading-snug text-[10px] min-h-[44px]"
                    value={col.label}
                    rows={1}
                    onChange={(e) => {
                      const newCols = [...columns];
                      // LOCALIZACIÃ“N SEGURA POR KEY ÃšNICA ORIGINAL
                      const idx = newCols.findIndex(c => c.key === col.key);
                      if (idx !== -1) {
                        newCols[idx] = { ...col, label: e.target.value };
                        onColumnsChange(newCols);
                      }
                    }}
                  />
                ) : (
                  <span className="block whitespace-normal break-words">{col.label}</span>
                )}
                    </div>
                  {(isAdmin && onColumnsChange) && (
                    <div className="flex flex-col gap-2 items-center bg-slate-800/50 p-2 rounded transition-colors group-hover/th:bg-slate-700/80">
                      <button 
                         title="Cambiar Ancho"
                         onClick={() => {
                           const newCols = [...columns];
                           const idx = newCols.findIndex(c => c.key === col.key);
                           newCols[idx] = cycleColWidth(col);
                           onColumnsChange(newCols);
                         }} className="bg-blue-600 hover:bg-blue-500 text-white rounded px-2 py-1 text-[8px] font-black w-full min-h-[44px] flex items-center justify-center">
                          {getColWidthOption(col).label}
                       </button>
                      {columns.length > 1 && (
                        <button 
                          title="Eliminar Columna"
                          onClick={() => {
                          if (!confirm(`¿Eliminar la columna "${col.label}" permanentemente?`)) return;
                          onColumnsChange(columns.filter(c => c.key !== col.key));
                        }} className="bg-red-600/70 hover:bg-red-500 text-white rounded px-2 py-1 min-h-[44px] w-full flex items-center justify-center">
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </th>
            ))}
            {(!readOnly || (isAdmin && onColumnsChange)) && <th className="px-4 py-3 border border-slate-800 w-32 text-center bg-slate-950 font-black">
              {(isAdmin && onColumnsChange) && (
                <button 
                  title="Añadir Columna"
                  onClick={() => {
                  const label = prompt('Nombre de la nueva columna (ej: Responsable, Meta, Avance):');
                  if (!label) return;
                  
                  // 1. GENERACIÃ“N SEGURA DE KEY ÃšNICA E INMUTABLE
                  const timestamp = Date.now().toString().slice(-4);
                  let baseKey = label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                  if (!baseKey || baseKey === 'nueva_columna') baseKey = `col_${timestamp}`;
                  
                  let finalKey = baseKey;
                  let counter = 1;
                  while (columns.some(c => c.key === finalKey)) {
                    finalKey = `${baseKey}_${counter}`;
                    counter++;
                  }
                  
                  // 2. RECONCILIACIÃ“N DE FILAS: Asegurar que todas las filas tengan la nueva key
                  const reconciledData = items.map(row => ({
                    ...row,
                    [finalKey]: ''
                  }));
                  
                  // 3. ACTUALIZACIÃ“N ATÃ“MICA DE ESTRUCTURA Y DATOS
                  onColumnsChange([...columns, { key: finalKey, label: label, colWidth: 'md' }]);
                  onChange(reconciledData);
                }} className="text-emerald-500 hover:text-emerald-400 transition-all hover:scale-125 p-2 flex items-center justify-center mx-auto min-w-[44px] min-h-[44px]">
                  <Plus size={20} />
                </button>
              )}
            </th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800 bg-slate-900/20">
          {items.map((item: any, idx: number) => {
            const isEmpty = Object.values(item).every(v => !v || String(v).trim() === '');
            return (
              <tr key={idx} className={cn("hover:bg-white/5 transition-colors group/row", isEmpty && idx === 0 && items.length === 1 ? "opacity-60" : "")}>
                <td className="px-4 py-2.5 border border-slate-800 text-center text-white/40 font-black text-[10px] bg-slate-950/20">{idx + 1}</td>
                {sanitizedColumns.filter(c => !c.key.startsWith('_')).map(col => (
                  <td key={col.key} className={cn("px-4 py-2.5 border border-slate-800 break-words relative", getColWidthOption(col).tdClass)}>
                    {col.type === 'select' ? (
                      <div className="relative group/select">
                        <select
                          className={cn(
                            "w-full focus:outline-none text-[10px] font-black h-10 transition-all appearance-none cursor-pointer px-4 pr-10 rounded-xl border flex items-center justify-center text-center shadow-lg", 
                            !canEditData ? "cursor-default" : "hover:border-indigo-500/50 hover:shadow-indigo-500/10",
                            item[col.key] === "SI" 
                              ? "bg-emerald-500 text-white border-emerald-400" 
                              : (item[col.key] === "" || !item[col.key])
                                ? "bg-slate-800/60 border-slate-700/50 text-slate-400 uppercase"
                                : "bg-slate-800 border-slate-700 text-white uppercase"
                          )}
                          value={item[col.key] || ''}
                          onChange={e => updateItem(idx, col.key, e.target.value)}
                          disabled={!canEditData || col.readOnly}
                        >
                          {(!col.hidePlaceholder && !col.options?.some((o:any) => (typeof o === 'object' ? o.value : o) === "")) && (
                            <option value="" className="bg-slate-900 leading-relaxed font-medium text-sm text-slate-500 italic">
                              {col.placeholder || "-- Seleccionar --"}
                            </option>
                          )}
                          {(col.options || []).map((opt: any) => {
                            const isObj = typeof opt === 'object';
                            const val = isObj ? opt.value : opt;
                            const lbl = isObj ? opt.label : opt;
                            return (
                              <option key={val} value={val} className="bg-slate-900 leading-relaxed font-medium text-sm text-white">
                                {lbl}
                              </option>
                            );
                          })}
                        </select>
                        <div className={cn(
                          "absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none transition-transform group-hover/select:scale-110",
                          item[col.key] === "SI" ? "text-white" : "text-slate-500"
                        )}>
                          <ChevronDown size={14} />
                        </div>
                      </div>
                    ) : ( 
                      // ... rest of code
                      <textarea
                        className={cn("bg-transparent w-full focus:outline-none resize-none overflow-hidden min-h-[44px] leading-relaxed text-white placeholder:text-white/5 text-[11px] transition-all",
                          (!canEditData || col.readOnly) ? "cursor-default" : "focus:text-emerald-400 cursor-text scrollbar-none")}
                        value={item[col.key] || ''}
                        onChange={e => {
                          updateItem(idx, col.key, e.target.value);
                          // Auto-resize
                          e.target.style.height = 'auto';
                          e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                        onFocus={e => {
                          e.target.style.height = 'auto';
                          e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                        placeholder="Escribir aquí..."
                        readOnly={!canEditData || col.readOnly}
                        rows={1}
                      />
                    )}
                  </td>
                ))}
                {effectiveCanEditRows && (
                  <td className="px-4 py-2 border border-slate-800 text-center w-24 min-w-[100px]">
                    <button 
                      onClick={() => removeItem(idx)} 
                      className="text-slate-600 hover:text-red-500 hover:scale-110 transition-all p-3 bg-slate-900 rounded-xl hover:shadow-lg hover:shadow-red-500/10 min-w-[44px] min-h-[44px] flex items-center justify-center mx-auto"
                      title="Eliminar Fila"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      </ScrollContainer>
      {effectiveCanEditRows && (
        <div className="p-4 border-t border-slate-800/50 bg-slate-950/30">
          <button 
            onClick={addItem} 
            className="flex items-center gap-2 bg-emerald-600 text-white px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-emerald-500 transition-all shadow-xl shadow-emerald-500/20 border border-emerald-400/20 active:scale-95 group"
          >
            <Plus size={16} className="group-hover:rotate-90 transition-transform duration-300" />
            Añadir Nuevo Registro
          </button>
        </div>
      )}
    </div>
  );
};

export const SWOTEditor = ({ data, onChange, readOnly, isAdmin }: { data: any, onChange: (data: any) => void, readOnly?: boolean, isAdmin?: boolean }) => {
  const swot = data || { strengths: '', opportunities: '', weaknesses: '', threats: '' };
  const canEditStructure = isAdmin; const canEditData = !readOnly || isAdmin;

  const update = (field: string, value: string) => {
    if (!canEditData) return;
    onChange({ ...swot, [field]: value });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      {[
        { id: 'strengths', label: data?.swot_labels?.strengths || 'Fortalezas', color: 'text-emerald-500', bg: 'bg-emerald-500/5', border: 'border-emerald-500/20' },
        { id: 'opportunities', label: data?.swot_labels?.opportunities || 'Oportunidades', color: 'text-blue-500', bg: 'bg-blue-500/5', border: 'border-blue-500/20' },
        { id: 'weaknesses', label: data?.swot_labels?.weaknesses || 'Debilidades', color: 'text-amber-500', bg: 'bg-amber-500/5', border: 'border-amber-500/20' },
        { id: 'threats', label: data?.swot_labels?.threats || 'Amenazas', color: 'text-red-500', bg: 'bg-red-500/5', border: 'border-red-500/20' }
      ].map(box => (
        <div key={box.id} className={cn("space-y-2 p-4 sm:p-5 rounded-2xl border transition-all", box.bg, box.border)}>
          <div className="flex justify-between items-center mb-1">
            {(isAdmin && !readOnly) ? (
              <input
                className={cn("text-[11px] font-black uppercase tracking-[0.3em] bg-transparent focus:outline-none focus:text-white border-b border-dashed border-transparent hover:border-white/20", box.color)}
                value={box.label}
                onChange={e => onChange({ ...(data || {}), swot_labels: { ...((data || {}).swot_labels || { strengths: 'Fortalezas', opportunities: 'Oportunidades', weaknesses: 'Debilidades', threats: 'Amenazas' }), [box.id]: e.target.value } })}
              />
            ) : (
              <label className={cn("text-[11px] font-black uppercase tracking-[0.3em] block", box.color)}>{box.label}</label>
            )}
          </div>
          <textarea
            className={cn("w-full bg-transparent text-white min-h-[100px] focus:outline-none leading-relaxed font-medium text-sm placeholder:text-white/10 resize-y",
              !canEditData ? "cursor-default" : "focus:ring-0")}
            value={(swot as any)[box.id]}
            onChange={e => update(box.id, e.target.value)}
            placeholder={`Escribir ${box.label.toLowerCase()}...`}
            readOnly={!canEditData}
          />
        </div>
      ))}
    </div>
  );
};

export const PremiumMonthSelector = ({ value, onChange, months }: { value: string, onChange: (val: string) => void, months: string[] }) => {
  return (
    <div className="relative group/month inline-block">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none bg-slate-900/60 backdrop-blur-md border border-slate-800 text-white px-6 py-4 rounded-[1.25rem] font-black uppercase text-[10px] tracking-[0.2em] hover:border-indigo-500/50 hover:shadow-2xl hover:shadow-indigo-500/10 focus:outline-none transition-all cursor-pointer min-w-[220px] shadow-lg pr-12"
      >
        {months.map(m => (
          <option key={m} value={m} className="bg-slate-900 text-white font-sans tracking-normal lowercase first-letter:uppercase">{m} 2026</option>
        ))}
      </select>
      <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-indigo-500 group-hover:scale-125 transition-transform duration-300">
        <ChevronDown size={16} strokeWidth={3} />
      </div>
    </div>
  );
};
export const Badge = ({ children, className, variant = 'default' }: any) => {
  const variants: Record<string, string> = {
    default: 'bg-white/10 text-white border-white/10',
    outline: 'bg-transparent border-white/20 text-white/60',
    success: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    warning: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    error: 'bg-red-500/10 text-red-500 border-red-500/20'
  };
  return (
    <span className={cn(
      "px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all",
      variants[variant] || variants.default,
      className
    )}>
      {children}
    </span>
  );
};

export const ScrollArea = ({ children, className }: any) => (
  <div className={cn("overflow-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent", className)}>
    {children}
  </div>
);


